import { useState, useEffect, useRef, useCallback } from 'react';
import { getEditors } from 'api/cansurvey/surveyApi';
import type { SurveyEditor } from 'types/survey';

interface UseEditorsReturn {
  editors: SurveyEditor[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useEditors = (surveyId: number | null): UseEditorsReturn => {
  const [editors, setEditors] = useState<SurveyEditor[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  const fetchEditors = useCallback(async () => {
    if (surveyId === null) return;

    setLoading(true);
    setError(null);
    try {
      const data = await getEditors(surveyId);
      if (mountedRef.current) {
        setEditors(data);
      }
    } catch (err) {
      console.error('[useEditors] Failed to fetch editors:', err);
      if (mountedRef.current) {
        setError(err instanceof Error ? err.message : 'Failed to load editors');
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [surveyId]);

  useEffect(() => {
    mountedRef.current = true;
    fetchEditors();

    return () => {
      mountedRef.current = false;
    };
  }, [fetchEditors]);

  return { editors, loading, error, refetch: fetchEditors };
};
